import {
  Button,
  breakpoints,
  ChoiceChips,
  Col,
  IconButton,
  ScrollArea,
  Select,
  Stack,
  Text,
  useTheme,
} from "@rally/ui-kit";
import { InviteBox } from "./invites/invite-box.tsx";
import { MyKeys } from "./keys/my-keys.tsx";
import { say } from "./localization/say.ts";
import { LanguageSetting } from "./me/language-setting.tsx";
import { Profile } from "./me/profile.tsx";
import { RecordingSetting } from "./me/recording-setting.tsx";
import { WhatTheServerDoes } from "./me/server-does.tsx";
import { SoundSetting } from "./me/sound-setting.tsx";
import { NotificationsSetting } from "./push/card.tsx";

type Section = { id: string; label: string };

export function Settings({
  visible,
  at,
  onGo,
  onClose,
  onChanged,
  onLeave,
  me,
  runsIt = false,
}: {
  visible: boolean;

  at: string;
  onGo: (section: string) => void;
  onClose: () => void;

  onChanged: () => void;
  onLeave: () => void;

  me?: string;

  runsIt?: boolean;
}) {
  const theme = useTheme();
  const sections: Section[] = [
    { id: "profile", label: say("you.profile") },
    { id: "preferences", label: say("you.preferences") },
    { id: "account", label: say("you.account") },
  ];
  const server: Section = { id: "server", label: say("you.server") };

  const narrow = theme.isAtMost(breakpoints.phone);
  const available = runsIt ? [...sections, server] : sections;
  const current = available.some((one) => one.id === at) ? at : "profile";
  if (!visible) return null;
  return (
    <Col testID="settings" style={{ flex: 1, overflow: "hidden" }}>
      <Col
        style={{
          gap: theme.space[3],
          paddingHorizontal: narrow ? theme.space[3] : theme.space[5],
          paddingTop: theme.space[4],
          paddingBottom: theme.space[3],
          borderBottomWidth: 1,
          borderBottomColor: theme.color["border.default"],
        }}
      >
        <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
          <Col style={{ flex: 1 }}>
            {narrow ? (
              <Select
                value={current}
                onChange={onGo}
                options={available.map((section) => ({ value: section.id, label: section.label }))}
              />
            ) : (
              <ChoiceChips
                value={current}
                onChange={onGo}
                options={available.map((section) => ({
                  value: section.id,
                  label: section.label,
                  testID: `settings-${section.id}`,
                }))}
              />
            )}
          </Col>
          <IconButton icon="close" ariaLabel={say("app.close")} onPress={onClose} testID="settings-close" />
        </Col>
      </Col>
      <ScrollArea
        contentContainerStyle={{
          padding: narrow ? theme.space[4] : theme.space[5],
          paddingBottom: theme.space[5],
        }}
      >
        <Stack gap={3}>
          <Button
            variant="ghost"
            icon="puzzle"
            onPress={() => onGo("integrations")}
            testID="account-integrations"
          >
            {say("you.integrations")}
          </Button>
          {current === "server" && runsIt ? (
            <Stack gap={5}>
              <RecordingSetting />
              <InviteBox place={{ kind: "server" }} />
              <WhatTheServerDoes />
            </Stack>
          ) : current === "account" ? (
            <MyKeys onLeave={onLeave} />
          ) : current === "profile" ? (
            <Profile onChanged={onChanged} />
          ) : current === "preferences" ? (
            <Stack gap={5}>
              <LanguageSetting />
              <NotificationsSetting />
              {me ? <SoundSetting me={me} /> : null}
            </Stack>
          ) : (
            <Text typeName="body" color="text.secondary">
              {say("you.nothingYet")(available.find((one) => one.id === current)?.label.toLowerCase() ?? "")}
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Col>
  );
}
